import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, X, Upload, ExternalLink, Share2, Globe } from 'lucide-react';
import { databaseService, storageService, appwriteConfig, ID } from '../../../lib/appwrite';

export default function SocialMedia() {
  const [socialMedias, setSocialMedias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingSocialMedia, setEditingSocialMedia] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    link: '',
    icon: null,
    iconPreview: null,
    isActive: true
  });

  const SOCIAL_MEDIA_COLLECTION = 'social_media';
  const STORAGE_BUCKET = appwriteConfig.storageId || 'reactbucket';

  useEffect(() => {
    loadSocialMedias();
  }, []);

  const loadSocialMedias = async () => {
    try {
      setLoading(true);
      const result = await databaseService.listDocuments(SOCIAL_MEDIA_COLLECTION);
      if (result.success) {
        setSocialMedias(result.data.documents);
      }
    } catch (error) {
      console.error('Error loading social media:', error);
      alert('Failed to load social media');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      link: '',
      icon: null,
      iconPreview: null,
      isActive: true
    });
    setEditingSocialMedia(null);
  };

  const openModal = (socialMedia = null) => {
    if (socialMedia) {
      setEditingSocialMedia(socialMedia);
      setFormData({
        name: socialMedia.name || '',
        link: socialMedia.link || '',
        icon: null,
        iconPreview: socialMedia.iconUrl || null,
        isActive: socialMedia.isActive !== false
      });
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleIconChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        alert('Icon size must be less than 2MB');
        return;
      }
      
      setFormData(prev => ({ ...prev, icon: file }));
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, iconPreview: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadIcon = async () => {
    if (!formData.icon) return null;
    
    try {
      const fileId = ID.unique();
      const result = await storageService.uploadFile(STORAGE_BUCKET, fileId, formData.icon);
      if (result.success) {
        return storageService.getFileView(STORAGE_BUCKET, result.data.$id);
      }
      return null;
    } catch (error) {
      console.error('Error uploading icon:', error);
      return null;
    }
  };

  const validateUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      alert('Social media name is required');
      return;
    }

    if (!formData.link.trim()) {
      alert('Social media link is required');
      return;
    }

    if (!validateUrl(formData.link)) {
      alert('Please enter a valid URL (including http:// or https://)');
      return;
    }

    try {
      setSaving(true);
      
      let iconUrl = editingSocialMedia?.iconUrl || null;
      
      if (formData.icon) {
        const uploadedUrl = await uploadIcon();
        if (uploadedUrl) {
          iconUrl = uploadedUrl;
        }
      }

      const socialMediaData = {
        name: formData.name.trim(),
        link: formData.link.trim(),
        iconUrl: iconUrl,
        isActive: formData.isActive,
        updatedAt: new Date().toISOString()
      };

      if (editingSocialMedia) {
        // Update existing social media
        const result = await databaseService.updateDocument(
          SOCIAL_MEDIA_COLLECTION,
          editingSocialMedia.$id,
          socialMediaData
        );
        if (result.success) {
          alert('Social media updated successfully!');
        }
      } else {
        // Create new social media
        socialMediaData.createdAt = new Date().toISOString();
        const result = await databaseService.createDocument(SOCIAL_MEDIA_COLLECTION, socialMediaData);
        if (result.success) {
          alert('Social media created successfully!');
        }
      }

      closeModal();
      loadSocialMedias();
    } catch (error) {
      console.error('Error saving social media:', error);
      alert('Failed to save social media');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (socialMedia) => {
    if (!confirm(`Are you sure you want to delete "${socialMedia.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const result = await databaseService.deleteDocument(SOCIAL_MEDIA_COLLECTION, socialMedia.$id);
      if (result.success) {
        alert('Social media deleted successfully!');
        loadSocialMedias();
      }
    } catch (error) {
      console.error('Error deleting social media:', error);
      alert('Failed to delete social media');
    }
  };

  const toggleStatus = async (socialMedia) => {
    try {
      const result = await databaseService.updateDocument(
        SOCIAL_MEDIA_COLLECTION,
        socialMedia.$id,
        { 
          isActive: !socialMedia.isActive,
          updatedAt: new Date().toISOString()
        }
      );
      if (result.success) {
        loadSocialMedias();
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#4C1D95]">Social Media Management</h1>
          <p className="text-gray-500 mt-1">Manage your social media links and icons</p>
        </div>
        <button
          onClick={() => openModal()}
          className="flex items-center gap-2 px-4 py-2 bg-[#4C1D95] text-white rounded-lg font-medium hover:bg-[#3b1575] transition-colors shadow-sm"
        >
          <Plus size={20} />
          Add Social Media
        </button>
      </div>

      {/* Social Media Grid */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-100 bg-gray-50/50 flex items-center gap-3">
          <Share2 className="w-5 h-5 text-[#4C1D95]" />
          <h2 className="text-lg font-bold text-gray-800">All Social Media</h2>
          <span className="text-xs font-medium bg-purple-100 text-purple-700 px-2 py-1 rounded-full">
            {socialMedias.length} Total
          </span>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="text-gray-500">Loading social media...</div>
            </div>
          ) : socialMedias.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {socialMedias.map((socialMedia) => (
                <div key={socialMedia.$id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center border">
                        {socialMedia.iconUrl ? (
                          <img 
                            src={socialMedia.iconUrl} 
                            alt={socialMedia.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <Globe className="w-6 h-6 text-gray-400" />
                        )}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                          {socialMedia.name}
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            socialMedia.isActive !== false 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-gray-100 text-gray-500'
                          }`}>
                            {socialMedia.isActive !== false ? 'Active' : 'Inactive'}
                          </span>
                        </h3>
                        <a 
                          href={socialMedia.link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1 mt-1"
                        >
                          <span className="truncate max-w-[200px]">{socialMedia.link}</span>
                          <ExternalLink size={12} />
                        </a>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => toggleStatus(socialMedia)}
                      className={`flex items-center gap-1 px-3 py-1.5 text-sm rounded-md transition-colors ${
                        socialMedia.isActive !== false
                          ? 'bg-yellow-50 text-yellow-600 hover:bg-yellow-100'
                          : 'bg-green-50 text-green-600 hover:bg-green-100'
                      }`}
                    >
                      {socialMedia.isActive !== false ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      onClick={() => openModal(socialMedia)}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 transition-colors"
                    >
                      <Edit size={14} />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(socialMedia)}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm bg-red-50 text-red-600 rounded-md hover:bg-red-100 transition-colors"
                    >
                      <Trash2 size={14} />
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Share2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Social Media Yet</h3>
              <p className="text-gray-500 mb-6">Get started by adding your first social media link</p>
              <button
                onClick={() => openModal()}
                className="flex items-center gap-2 px-4 py-2 bg-[#4C1D95] text-white rounded-lg font-medium hover:bg-[#3b1575] transition-colors mx-auto"
              >
                <Plus size={20} />
                Add First Social Media
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">
                  {editingSocialMedia ? 'Edit Social Media' : 'Add New Social Media'}
                </h2>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Icon Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Social Media Icon
                </label>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center border-2 border-dashed border-gray-300">
                    {formData.iconPreview ? (
                      <img 
                        src={formData.iconPreview} 
                        alt="Preview"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Globe className="w-8 h-8 text-gray-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <label className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                      <Upload size={16} />
                      <span className="text-sm font-medium">
                        {formData.icon ? 'Change Icon' : 'Upload Icon'}
                      </span>
                      <input 
                        type="file" 
                        accept="image/*" 
                        onChange={handleIconChange} 
                        className="hidden" 
                      />
                    </label>
                    <p className="text-xs text-gray-500 mt-1">
                      Max 2MB. PNG, JPG, SVG recommended.
                    </p>
                  </div>
                </div>
              </div>

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Social Media Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g. Facebook, Twitter, LinkedIn"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4C1D95]/20 focus:border-[#4C1D95] outline-none transition-all"
                  required
                />
              </div>

              {/* Link */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Social Media Link *
                </label>
                <input
                  type="url"
                  value={formData.link}
                  onChange={(e) => setFormData(prev => ({ ...prev, link: e.target.value }))}
                  placeholder="https://facebook.com/yourprofile"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4C1D95]/20 focus:border-[#4C1D95] outline-none transition-all"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Include the full URL with https://
                </p>
              </div>

              {/* Active Status */}
              <div>
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                    className="w-4 h-4 text-[#4C1D95] border-gray-300 rounded focus:ring-[#4C1D95]/20"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Active (visible on website)
                  </span>
                </label>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-[#4C1D95] text-white rounded-lg font-medium hover:bg-[#3b1575] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Save size={18} />
                  {saving ? 'Saving...' : (editingSocialMedia ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}